import { filterRoutesForPlatform } from 'platform/mobileScope';
import { lazy } from 'react';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import { tabPanelLoadableOptions } from 'ui-component/tabs/tabPanelLoadableOptions';
import AuthGuard from 'utils/route-guard/AuthGuard';
import PriorityGuard from 'utils/route-guard/PriorityGuard';
import PermissionGuard from 'utils/route-guard/PermissionGuard';
import AccountModuleGuard from 'utils/route-guard/AccountModuleGuard';
import AdminGuard from 'utils/route-guard/AdminGuard';
import FunnerInternalGuard from 'utils/route-guard/FunnerInternalGuard';
import { RouteObject, Navigate, Outlet } from 'react-router-dom';
import FilesListLayout from 'views/files/FilesListLayout';
import CreateEditTemplate from 'views/messages-templates/createEdit';
import RouteErrorBoundary from 'views/pages/maintenance/RouteErrorBoundary';

// sample page routing
// const SamplePage = Loadable(lazy(() => import('views/sample-page')));
const ComingSoon = Loadable(lazy(() => import('views/pages/maintenance/ComingSoon/ComingSoon2')));

//* External App
const ExternalAppPage = Loadable(lazy(() => import('views/external-app')));

//* Boards (dashboards)
const BoardsLayout = Loadable(lazy(() => import('views/boards/BoardsLayout')));
const BoardListPage = Loadable(lazy(() => import('views/boards/BoardListPage')));
const BoardViewPage = Loadable(lazy(() => import('views/boards/BoardViewPage')));
const InfoCenter = Loadable(lazy(() => import('views/info-center')));
/** QA Engine — הסר את הייבוא והנתיב למטה אם מוחקים את `src/features/QAEngine`. */
const QAMasterPage = Loadable(lazy(() => import('features/QAEngine/QAMasterPage')));
const MapVisualizerPage = Loadable(lazy(() => import('features/MapVisualizer')));

//* Signer (PDF signing wizard)
const SignerWizard = Loadable(lazy(() => import('views/signer')), tabPanelLoadableOptions);

//* Chats
const Chats = Loadable(lazy(() => import('views/chats')), tabPanelLoadableOptions);
//* Constant use
const FavouriteMessages = Loadable(lazy(() => import('views/favourite-messages')));
//* Contacts
const ConversationsLog = Loadable(lazy(() => import('views/conversations-log')));
const Contacts = Loadable(lazy(() => import('views/contacts')));
const ContactsChat = Loadable(lazy(() => import('views/contacts/chat')));
const SingleChatUserPage = Loadable(lazy(() => import('views/contacts/chat/single-user')));
const ImportChatUsersFromFile = Loadable(lazy(() => import('views/contacts/chat/import-wizard')));
const ContactsCustomers = Loadable(lazy(() => import('views/contacts/customers')));
const ContactsPriority = Loadable(lazy(() => import('views/contacts/priority-contacts')));
const ContactsPriorityCustomers = Loadable(lazy(() => import('views/contacts/priority-customers')));
const SyncPriorityContacts = Loadable(lazy(() => import('views/contacts/chat/priority-sync')));

//* User config
const UserConfigurations = Loadable(lazy(() => import('views/configurations')));
const UserChatConfigurations = Loadable(lazy(() => import('views/configurations/chat')));
const UserChatKeyboardConfigurations = Loadable(lazy(() => import('views/configurations/chat-keyboard')));
const UserChatSpellConfigurations = Loadable(lazy(() => import('views/configurations/chat-spell')));
const UserSystemConfigurations = Loadable(lazy(() => import('views/configurations/system')));
const UserNotificationConfigurations = Loadable(lazy(() => import('views/configurations/notifications')));
// const Profile = Loadable(lazy(() => import('views/profile/index_old')));
const UserProfile = Loadable(lazy(() => import('views/profile')));
const PersonalSignatureConfigurations = Loadable(lazy(() => import('views/profile/PersonalSignaturePanel')));

//** Templates
const MessagesTemplates = Loadable(lazy(() => import('views/messages-templates')));
const TemplatesList = Loadable(lazy(() => import('views/messages-templates/list')));
const DraftMessagesTemplates = Loadable(lazy(() => import('views/messages-templates/drafts')));
const PredefinedMessagesTemplates = Loadable(lazy(() => import('views/messages-templates/predefined')));
const TemplatesDefaultSettings = Loadable(lazy(() => import('views/messages-templates/default-settings')));
const TemplatesCatalog = Loadable(lazy(() => import('views/messages-templates/catalog')));
//** System management
//* Company info
const CompanyIndex = Loadable(lazy(() => import('views/company')));
const CompanyDetails = Loadable(lazy(() => import('views/company/details')));
const ConversationCompanyHub = Loadable(lazy(() => import('views/company/ConversationCompanyHub')));
const ConversationSettingsTabContent = Loadable(lazy(() => import('views/company/ConversationSettingsTabContent')));
const FacebookSettingsPage = Loadable(lazy(() => import('views/company/facebook')), tabPanelLoadableOptions);
const FacebookWhatsAppManagement = Loadable(
  lazy(() => import('views/company/facebook/whatsapp-management')),
  tabPanelLoadableOptions,
);
const FacebookUpdatesPage = Loadable(
  lazy(() => import('views/company/facebook/facebook-updates')),
  tabPanelLoadableOptions,
);
const CompanyAbout = Loadable(lazy(() => import('views/company/about')));
const SystemExtensionsIndex = Loadable(lazy(() => import('views/system-extensions')));
const ManageSystemExtensions = Loadable(lazy(() => import('views/system-extensions/manage')));
const ChatWindowExtensions = Loadable(lazy(() => import('views/system-extensions/chat-window')));
const ConversationActionMenuSettings = Loadable(lazy(() => import('views/system-extensions/action-menu')));
const CompanyPermissions = Loadable(lazy(() => import('views/company/permissions')));
const CompanyPermissionsBatch = Loadable(
  lazy(() => import('views/company/permissions/batch')),
  tabPanelLoadableOptions,
);
const CompanyViewsVisibility = Loadable(lazy(() => import('views/company/views-visibility')));
const CompanyViewsVisibilityBatch = Loadable(
  lazy(() => import('views/company/views-visibility/batch')),
  tabPanelLoadableOptions,
);
const CompanySubscriptionDetails = Loadable(lazy(() => import('views/company/subscription/SubscriptionDetails')));
const CompanyInvoicesPayments = Loadable(lazy(() => import('views/company/subscription/InvoicesPayments')));
const AccountIndex = Loadable(lazy(() => import('views/account')));
const AccountUsability = Loadable(lazy(() => import('views/account/AccountUsability')));
// const CompanyDocsSettings = Loadable(lazy(() => import('views/company/docs')));
//* App users
const ApplicationUsers = Loadable(lazy(() => import('views/application-users')));
const UsersList = Loadable(lazy(() => import('views/application-users/UsersList')));
const ApplicationUser = Loadable(lazy(() => import('views/application-users/user')));
const CreateApplicationUser = Loadable(lazy(() => import('views/application-users/create')));

const Labels = Loadable(lazy(() => import('views/labels')));
const Files = Loadable(lazy(() => import('views/files')));
const SimpleTemplatesList = Loadable(lazy(() => import('views/files/simple-templates')));
//* Advanced use
//* Mass messaging groups
const BroadcastListsTab = Loadable(lazy(() => import('views/mass-messaging/groups/lists')));
const MassMessagingGroupCreateEdit = Loadable(
  lazy(() => import('views/mass-messaging/groups/create-edit-group')),
);
//* Mass messaging tasks
const MassMessagingTasks = Loadable(lazy(() => import('views/mass-messaging/tasks')));
const BroadcastUnsubscribeSettings = Loadable(
  lazy(() => import('views/mass-messaging/tasks/BroadcastUnsubscribeSettings')),
);
const MassMessagingTasksList = Loadable(lazy(() => import('views/mass-messaging/tasks/TasksList')));
const CreateEditMassMessagingTask = Loadable(
  lazy(() => import('views/mass-messaging/tasks/create-edit-task')),
);
const MassMessagingTaskLogs = Loadable(lazy(() => import('views/mass-messaging/tasks/logs')));
//* Mass messaging mailing
const WhatsappMailing = Loadable(lazy(() => import('views/mass-messaging/mail')));
//TODO Workflows
const ManageBotsWrapper = Loadable(lazy(() => import('views/bots/ManageBotsWrapper')));
//* Digital Signature
const DocsSign = Loadable(lazy(() => import('views/docs-sign')));
const FlowiseBuilderPage = Loadable(lazy(() => import('views/flowise')));
//* AI Features
const AIAnalyze = Loadable(lazy(() => import('views/ai/ai-analyze')));
const OrdersAgentPage = Loadable(lazy(() => import('views/orders-agent')));
const OrdersAgentCatalogTab = Loadable(lazy(() => import('views/orders-agent/catalog')));
const OrdersAgentRulesTab = Loadable(lazy(() => import('views/orders-agent/rules')));
const OrdersAgentTaxonomyTab = Loadable(lazy(() => import('views/orders-agent/taxonomy')));
const OrdersAgentLearningTab = Loadable(lazy(() => import('views/orders-agent/learning')));
const OrdersAgentLessonsTab = Loadable(lazy(() => import('views/orders-agent/lessons')));
const OrdersAgentMonitorTab = Loadable(lazy(() => import('views/orders-agent/monitor')));
const OrdersAgentSettingsTab = Loadable(lazy(() => import('views/orders-agent/settings')));
const OrdersAgentSimulatorTab = Loadable(lazy(() => import('views/orders-agent/simulator')));
const Telephony = Loadable(lazy(() => import('views/telephony')));
const TelephonyPhoneCallsTab = Loadable(lazy(() => import('views/telephony/phone-calls')));
//* Integrations
const IntegrationsPage = Loadable(lazy(() => import('views/integrations')), tabPanelLoadableOptions);
const WebhooksPage = Loadable(lazy(() => import('views/integrations/webhooks')), tabPanelLoadableOptions);
const IntegrationsTabPage = Loadable(lazy(() => import('views/integrations/integrations')), tabPanelLoadableOptions);
const IntegrationsConnectionsCenterPage = Loadable(
  lazy(() => import('views/integrations/connections')),
  tabPanelLoadableOptions,
);
const ApiKeysPage = Loadable(lazy(() => import('views/integrations/api-keys')), tabPanelLoadableOptions);
const SystemSettingsLayout = Loadable(lazy(() => import('views/system-settings/SystemSettingsLayout')));
const SocketMonitorPage = Loadable(
  lazy(() => import('views/system-settings/socket-monitor')),
  tabPanelLoadableOptions,
);
const ChatExportLogPage = Loadable(
  lazy(() => import('views/system-settings/chat-export-log')),
  tabPanelLoadableOptions,
);
const SecurityPrivacyLayout = Loadable(lazy(() => import('views/security-privacy/SecurityPrivacyLayout')));
const SecuritySessionsLogPage = Loadable(
  lazy(() => import('views/security-privacy/sessions')),
  tabPanelLoadableOptions,
);
const SecuritySensitiveActionsPage = Loadable(
  lazy(() => import('views/security-privacy/sensitive-actions')),
  tabPanelLoadableOptions,
);
//* Fireberry
const FireberrySettingsPage = Loadable(lazy(() => import('views/fireberry')), tabPanelLoadableOptions);
const FireberryCompany = Loadable(lazy(() => import('views/fireberry/company')), tabPanelLoadableOptions);
const FireberryClientsAndContacts = Loadable(lazy(() => import('views/fireberry/clients')), tabPanelLoadableOptions);
const FireberryObjectsPage = Loadable(lazy(() => import('views/fireberry/objects')), tabPanelLoadableOptions);
const FireberrySync = Loadable(lazy(() => import('views/fireberry/sync')), tabPanelLoadableOptions);
//* priority
const PrioritySettingsPage = Loadable(lazy(() => import('views/priority/priority-settings-page')), tabPanelLoadableOptions);
const PriorityAppLinks = Loadable(lazy(() => import('views/priority/priority-settings-page/app-links')), tabPanelLoadableOptions);
const PriorityContactSync = Loadable(
  lazy(() => import('views/priority/priority-settings-page/contact-sync')),
  tabPanelLoadableOptions,
);
const PriorityConfigurations = Loadable(
  lazy(() => import('views/priority/priority-settings-page/configurations')),
  tabPanelLoadableOptions,
);
const PriorityFields = Loadable(lazy(() => import('views/priority/priority-settings-page/fields')), tabPanelLoadableOptions);
const PriorityMassActions = Loadable(
  lazy(() => import('views/priority/priority-settings-page/mass-actions')),
  tabPanelLoadableOptions,
);
const ProceduresAndFormats = Loadable(
  lazy(() => import('views/priority/priority-settings-page/procedures-and-formats')),
  tabPanelLoadableOptions,
);
const PrCompanies = Loadable(lazy(() => import('views/priority/priority-settings-page/companies')), tabPanelLoadableOptions);
const PrCompanyProcedures = Loadable(
  lazy(() => import('views/priority/priority-settings-page/company-procedures')),
  tabPanelLoadableOptions,
);
const PrConnections = Loadable(lazy(() => import('views/priority/priority-settings-page/connections')), tabPanelLoadableOptions);
const PriorityAutoDocSend = Loadable(
  lazy(() => import('views/priority/priority-settings-page/auto-doc-send')),
  tabPanelLoadableOptions,
);
const PrioritySessionSync = Loadable(
  lazy(() => import('views/priority/priority-settings-page/session-sync')),
  tabPanelLoadableOptions,
);
const PriorityDocStatsLog = Loadable(
  lazy(() => import('views/priority/priority-settings-page/doc-stats-log')),
  tabPanelLoadableOptions,
);

//TODO
const AppKanban = Loadable(lazy(() => import('views/kanban')));
const AppKanbanBacklogs = Loadable(lazy(() => import('views/kanban/Backlogs')));
const AppKanbanBoard = Loadable(lazy(() => import('views/kanban/Board')));
const AppCalendar = Loadable(lazy(() => import('views/calendar')));

//* Links Tree (used for mapping links inside the blocks)
const LinksTree = Loadable(lazy(() => import('views/links-tree')));
// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes: RouteObject = {
  path: '/',
  element: (
    <AuthGuard>
      <MainLayout />
    </AuthGuard>
  ),
  errorElement: <RouteErrorBoundary />,
  //* באפליקציית המובייל נשארים רק ראוטים שבסקופ (platform/mobileScope); בווב הרשימה מלאה
  children: filterRoutesForPlatform([
    //* Chats — route אחד למניעת remount של רשימת השיחות והפאנל המרכזי במעבר בין תצוגות
    {
      path: '/chats/*',
      element: <PermissionGuard module="messages"><Chats /></PermissionGuard>,
    },
    //* Constant use
    {
      path: '/constant-use',
      element: <LinksTree />,
    },
    {
      path: '/favourite-messages',
      element: <FavouriteMessages />,
    },
    {
      path: '/conversations-log',
      element: <Navigate to="/company/conversation/conversations-log" replace />,
    },
    {
      path: '/contacts',
      element: <PermissionGuard module="contacts"><Contacts /></PermissionGuard>,
      children: [
        {
          path: 'chat',
          element: <ContactsChat />,
        },
        {
          path: 'customers',
          element: <ContactsCustomers />,
        },
        {
          path: 'import',
          element: <ImportChatUsersFromFile />,
        },
        {
          path: 'priority-contacts',
          element: <ContactsPriority />,
        },
        {
          path: 'priority-customers',
          element: <ContactsPriorityCustomers />,
        },
        {
          path: 'priority-sync',
          element: <SyncPriorityContacts />,
        },
      ],
    },
    { path: '/contacts/chat/:chatUserId', element: <PermissionGuard module="contacts"><SingleChatUserPage /></PermissionGuard> },
    //** Templates
    {
      path: '/templates',
      element: <PermissionGuard module="templates"><MessagesTemplates /></PermissionGuard>,
      children: [
        {
          index: true,
          element: <Navigate to="/templates/list" replace />,
        },
        {
          path: 'home',
          element: <Navigate to="/templates/list" replace />,
        },
        {
          path: 'catalog',
          element: <TemplatesCatalog />,
        },
        {
          path: 'list',
          element: <TemplatesList key="all-templates" />,
        },
        {
          path: 'drafts',
          element: <DraftMessagesTemplates />,
        },
        {
          path: 'rejected',
          element: <TemplatesList key="all-templates" />,
        },
        {
          path: 'default-settings',
          element: <TemplatesDefaultSettings />,
        },
        {
          path: 'predefined',
          element: <PredefinedMessagesTemplates />,
        },
        {
          path: 'simple',
          element: <SimpleTemplatesList />,
        },
      ],
    },
    { path: '/templates/create', element: <PermissionGuard module="templates"><CreateEditTemplate /></PermissionGuard> },
    { path: '/templates/edit/:id', element: <PermissionGuard module="templates"><CreateEditTemplate /></PermissionGuard> },
    {
      path: '/templates/drafts/:id',
      element: <PermissionGuard module="templates"><CreateEditTemplate templateType="draft" /></PermissionGuard>,
    },
    {
      path: '/customization',
      element: <UserConfigurations />,
      children: [
        {
          path: 'profile',
          element: <UserProfile />,
        },
        {
          path: 'personal-signature',
          element: <PersonalSignatureConfigurations />,
        },
        {
          path: 'chat',
          element: <UserChatConfigurations />,
        },
        {
          path: 'chat-keyboard',
          element: <UserChatKeyboardConfigurations />,
        },
        {
          path: 'chat-spell',
          element: <UserChatSpellConfigurations />,
        },
        {
          path: 'style',
          element: <UserSystemConfigurations />,
        },
        {
          path: 'notifications',
          element: <UserNotificationConfigurations />,
        },
        { path: 'extension', element: <Navigate to="/customization/profile" replace /> },
      ],
    },
    {
      path: '/profile',
      element: <UserProfile />,
    },
    {
      path: '/account',
      element: <AccountIndex />,
      children: [
        { index: true, element: <Navigate to="about" replace /> },
        { path: 'about', element: <CompanyAbout /> },
        { path: 'subscription-details', element: <CompanySubscriptionDetails /> },
        { path: 'invoices-payments', element: <CompanyInvoicesPayments /> },
        { path: 'subscription-payments', element: <Navigate to="/account/subscription-details" replace /> },
        { path: 'usability', element: <AccountUsability /> },
        { path: 'features', element: <Navigate to="/account/about" replace /> },
      ],
    },
    {
      path: '/interface',
      element: <SystemExtensionsIndex basePath="/interface" />,
      children: [
        { index: true, element: <Navigate to="manage" replace /> },
        { path: 'manage', element: <ManageSystemExtensions /> },
        { path: 'chat-window', element: <ChatWindowExtensions /> },
        { path: 'action-menu', element: <ConversationActionMenuSettings /> },
      ],
    },
    //* System management
    {
      path: '/system-management',
      element: <LinksTree />,
    },
    {
      path: '/company/extensions',
      element: <Navigate to="/interface/manage" replace />,
    },
    {
      path: '/company/chat-customization',
      element: <Navigate to="/interface/chat-window" replace />,
    },
    { path: '/system-extensions', element: <Navigate to="/interface/manage" replace /> },
    { path: '/system-extensions/manage', element: <Navigate to="/interface/manage" replace /> },
    { path: '/system-extensions/chat-window', element: <Navigate to="/interface/chat-window" replace /> },
    { path: '/system-extensions/action-menu', element: <Navigate to="/interface/action-menu" replace /> },
    { path: '/system-settings/extensions', element: <Navigate to="/interface/manage" replace /> },
    { path: '/system-settings/extensions/manage', element: <Navigate to="/interface/manage" replace /> },
    {
      path: '/system-settings/extensions/chat-window',
      element: <Navigate to="/interface/chat-window" replace />,
    },
    {
      path: '/system-settings/extensions/action-menu',
      element: <Navigate to="/interface/action-menu" replace />,
    },
    {
      path: '/company/conversation',
      element: <ConversationCompanyHub />,
      children: [
        { index: true, element: <Navigate to="settings" replace /> },
        { path: 'settings', element: <ConversationSettingsTabContent /> },
        { path: 'automatic-updates', element: <Navigate to="settings" replace /> },
        { path: 'conversations-log', element: <ConversationsLog omitBreadcrumbs /> },
      ],
    },
    {
      path: '/company',
      element: <CompanyIndex />,
      children: [
        { index: true, element: <Navigate to="details" replace /> },
        { path: 'details', element: <CompanyDetails /> },
        {
          path: 'workflow-guide',
          element: <Navigate to="/info-center?tab=workflow-flow-statuses" replace />,
        },
        {
          path: 'facebook-notifications',
          element: <Navigate to="/system-settings/facebook-notifications" replace />,
        },
        { path: 'about', element: <Navigate to="/account/about" replace /> },
        {
          path: 'contact-arrival-sources',
          element: <Navigate to="/contacts/chat" replace />,
        },
        { path: 'permissions', element: <Navigate to="/users/permissions" replace /> },
        { path: 'subscription-payments', element: <Navigate to="/account/subscription-details" replace /> },
        // { path: 'documents', element: <CompanyDocsSettings /> },
      ],
    },
    {
      path: '/users',
      element: <ApplicationUsers />,
      children: [
        { index: true, element: <UsersList /> },
        { path: 'permissions', element: <CompanyPermissions /> },
        { path: 'views', element: <CompanyViewsVisibility /> },
      ],
    },
    {
      path: '/users/permissions-batch',
      element: <ApplicationUsers />,
      children: [{ index: true, element: <CompanyPermissionsBatch /> }],
    },
    {
      path: '/users/views-batch',
      element: <ApplicationUsers />,
      children: [{ index: true, element: <CompanyViewsVisibilityBatch /> }],
    },
    {
      path: '/users/create',
      element: <CreateApplicationUser />,
    },
    {
      path: '/users/:id',
      element: <ApplicationUser />,
    },
    {
      path: '/labels',
      element: <Labels />,
    },
    {
      path: '/drive',
      element: <Files />,
      children: [
        {
          path: 'images',
          element: <FilesListLayout type="image" />,
        },
        {
          path: 'videos',
          element: <FilesListLayout type="video" />,
        },
        {
          path: 'documents',
          element: <FilesListLayout type="document" />,
        },
        {
          path: 'templates',
          element: <Navigate to="/templates/simple" replace />,
        },
      ],
    },
    //* Advanced use
    {
      path: '/advanced-use',
      element: <LinksTree />,
    },
    {
      path: '/signer',
      element: <PermissionGuard module="signing"><SignerWizard /></PermissionGuard>,
    },
    {
      path: '/broadcasts-lists',
      element: <Navigate to="/broadcasts/lists" replace />,
    },
    {
      path: '/broadcasts-lists/lists',
      element: <Navigate to="/broadcasts/lists" replace />,
    },
    {
      path: '/broadcasts-lists/unsubscribe-settings',
      element: <Navigate to="/broadcasts/unsubscribe-settings" replace />,
    },
    {
      path: '/broadcasts-lists/create',
      element: <PermissionGuard module="broadcast"><MassMessagingGroupCreateEdit /></PermissionGuard>,
    },
    {
      path: '/broadcasts-lists/edit/:id',
      element: <PermissionGuard module="broadcast"><MassMessagingGroupCreateEdit /></PermissionGuard>,
    },
    {
      path: '/broadcasts',
      element: <PermissionGuard module="broadcast"><MassMessagingTasks /></PermissionGuard>,
      children: [
        {
          index: true,
          element: <Navigate to="/broadcasts/lists" replace />,
        },
        {
          path: 'home',
          element: <Navigate to="/broadcasts/lists" replace />,
        },
        {
          path: 'lists',
          element: <BroadcastListsTab />,
        },
        {
          path: 'unsubscribe-settings',
          element: <BroadcastUnsubscribeSettings />,
        },
        {
          path: 'planned',
          element: <MassMessagingTasksList listType="planned" key="planned" />,
        },
        {
          path: 'running',
          element: <MassMessagingTasksList listType="running" key="running" />,
        },
        {
          path: 'finished',
          element: <MassMessagingTasksList listType="ended" key="ended" />,
        },
      ],
    },
    {
      path: '/broadcasts/create',
      element: <PermissionGuard module="broadcast"><CreateEditMassMessagingTask /></PermissionGuard>,
    },
    {
      path: '/broadcasts/edit/:id',
      element: <PermissionGuard module="broadcast"><CreateEditMassMessagingTask /></PermissionGuard>,
    },
    {
      path: '/broadcast-logs/:id',
      element: <PermissionGuard module="broadcast"><MassMessagingTaskLogs /></PermissionGuard>,
    },
    {
      path: '/mail-whatsapp',
      element: <WhatsappMailing />,
    },
    {
      path: '/workflows',
      element: <PermissionGuard module="bots"><ManageBotsWrapper /></PermissionGuard>,
    },
    {
      path: '/flowise',
      element: (
        <AccountModuleGuard module="aiBots">
          <PermissionGuard module="bots">
            <FlowiseBuilderPage />
          </PermissionGuard>
        </AccountModuleGuard>
      ),
    },
    {
      path: '/orders-agent',
      element: <PermissionGuard module="bots"><OrdersAgentPage /></PermissionGuard>,
      children: [
        { index: true, element: <Navigate to="catalog" replace /> },
        { path: 'catalog', element: <OrdersAgentCatalogTab /> },
        { path: 'rules', element: <OrdersAgentRulesTab /> },
        { path: 'taxonomy', element: <OrdersAgentTaxonomyTab /> },
        { path: 'learning', element: <OrdersAgentLearningTab /> },
        { path: 'lessons', element: <OrdersAgentLessonsTab /> },
        { path: 'monitor', element: <OrdersAgentMonitorTab /> },
        { path: 'simulator', element: <OrdersAgentSimulatorTab /> },
        { path: 'settings', element: <OrdersAgentSettingsTab /> },
      ],
    },
    //* AI Features
    {
      path: '/ai',
      element: <LinksTree />,
    },
    {
      path: '/ai-analyze',
      element: <AIAnalyze />,
    },
    {
      path: '/telephony',
      element: <Telephony />,
      children: [
        { index: true, element: <Navigate to="phone-calls" replace /> },
        { path: 'phone-calls', element: <TelephonyPhoneCallsTab /> },
      ],
    },
    //* Integrations
    {
      path: '/webhooks',
      element: <IntegrationsPage />,
      children: [
        {
          path: 'connections',
          element: <IntegrationsConnectionsCenterPage />,
        },
        {
          path: 'webhooks',
          element: <WebhooksPage />,
        },
        {
          path: 'integrations',
          element: <IntegrationsTabPage />,
        },
        {
          path: 'api-keys',
          element: <ApiKeysPage />,
        },
      ],
    },
    {
      path: '/security-privacy',
      element: (
        <AdminGuard>
          <SecurityPrivacyLayout />
        </AdminGuard>
      ),
      children: [
        { index: true, element: <Navigate to="/security-privacy/sessions" replace /> },
        { path: 'sessions', element: <SecuritySessionsLogPage /> },
        { path: 'sensitive-actions', element: <SecuritySensitiveActionsPage /> },
        { path: 'chat-export-log', element: <ChatExportLogPage /> },
      ],
    },
    {
      path: '/system-settings',
      element: <SystemSettingsLayout />,
      children: [
        { index: true, element: <Navigate to="facebook-notifications" replace /> },
        {
          path: 'facebook-notifications',
          element: <FacebookSettingsPage embedShell basePath="/system-settings/facebook-notifications" />,
          children: [
            { index: true, element: <Navigate to="whatsapp-management" replace /> },
            { path: 'whatsapp-management', element: <FacebookWhatsAppManagement /> },
            { path: 'updates', element: <FacebookUpdatesPage /> },
          ],
        },
        {
          path: 'priority',
          element: (
            <PriorityGuard>
              <PrioritySettingsPage embedShell basePath="/system-settings/priority" />
            </PriorityGuard>
          ),
          children: [
            { index: true, element: <Navigate to="app-links" replace /> },
            { path: 'app-links', element: <PriorityAppLinks /> },
            { path: 'configurations', element: <PriorityConfigurations /> },
            { path: 'contact-sync', element: <PriorityContactSync /> },
            { path: 'fields', element: <PriorityFields /> },
            { path: 'mass-actions', element: <PriorityMassActions /> },
            { path: 'companies', element: <PrCompanies /> },
            { path: 'company-procedures', element: <PrCompanyProcedures /> },
            { path: 'procedures', element: <ProceduresAndFormats /> },
            { path: 'connections', element: <PrConnections /> },
            { path: 'auto-doc-send', element: <PriorityAutoDocSend /> },
            { path: 'doc-stats-log', element: <PriorityDocStatsLog /> },
            {
              path: 'session-sync',
              element: (
                <FunnerInternalGuard redirectTo="/system-settings/priority/app-links">
                  <PrioritySessionSync />
                </FunnerInternalGuard>
              ),
            },
          ],
        },
        {
          path: 'fireberry',
          element: <FireberrySettingsPage embedShell basePath="/system-settings/fireberry" />,
          children: [
            { index: true, element: <Navigate to="company" replace /> },
            { path: 'company', element: <FireberryCompany /> },
            { path: 'clients-and-contacts', element: <FireberryClientsAndContacts /> },
            { path: 'objects', element: <FireberryObjectsPage /> },
            { path: 'sync', element: <FireberrySync /> },
          ],
        },
        { path: 'connections', element: <IntegrationsConnectionsCenterPage /> },
        { path: 'webhooks', element: <WebhooksPage /> },
        { path: 'api-keys', element: <ApiKeysPage /> },
        { path: 'socket-monitor', element: <SocketMonitorPage /> },
        {
          path: 'chat-export-log',
          element: <Navigate to="/security-privacy/chat-export-log" replace />,
        },
        {
          path: 'webhooks/v2',
          element: <Navigate to="/system-settings/webhooks" replace />,
        },
        {
          path: 'webhooks-v2',
          element: <Navigate to="/system-settings/webhooks" replace />,
        },
        {
          path: 'webhooks/connections',
          element: <Navigate to="/system-settings/connections" replace />,
        },
        {
          path: 'webhooks/webhooks',
          element: <Navigate to="/system-settings/webhooks" replace />,
        },
        {
          path: 'webhooks/integrations',
          element: <Navigate to="/webhooks/integrations" replace />,
        },
      ],
    },
    {
      path: '/fireberry',
      element: <FireberrySettingsPage />,
      children: [
        {
          path: 'company',
          element: <FireberryCompany />,
        },
        {
          path: 'clients-and-contacts',
          element: <FireberryClientsAndContacts />,
        },
        {
          path: 'objects',
          element: <FireberryObjectsPage />,
        },
        {
          path: 'sync',
          element: <FireberrySync />,
        },
      ],
    },
    {
      path: '/priority',
      element: (
        <PriorityGuard>
          <PrioritySettingsPage />
        </PriorityGuard>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="app-links" replace />,
        },
        {
          path: 'app-links',
          element: <PriorityAppLinks />,
        },
        {
          path: 'configurations',
          element: <PriorityConfigurations />,
        },
        {
          path: 'contact-sync',
          element: <PriorityContactSync />,
        },
        {
          path: 'fields',
          element: <PriorityFields />,
        },
        {
          path: 'mass-actions',
          element: <PriorityMassActions />,
        },
        {
          path: 'companies',
          element: <PrCompanies />,
        },
        {
          path: 'company-procedures',
          element: <PrCompanyProcedures />,
        },
        {
          path: 'procedures',
          element: <ProceduresAndFormats />,
        },
        {
          path: 'connections',
          element: <PrConnections />,
        },
        {
          path: 'auto-doc-send',
          element: <PriorityAutoDocSend />,
        },
        {
          path: 'doc-stats-log',
          element: <PriorityDocStatsLog />,
        },
        {
          path: 'session-sync',
          element: (
            <FunnerInternalGuard redirectTo="/priority/app-links">
              <PrioritySessionSync />
            </FunnerInternalGuard>
          ),
        },
      ],
    },
    {
      path: '/kanban',
      element: <AppKanban />,
      // element: <ComingSoon />,
      children: [
        {
          path: 'backlogs',
          element: <AppKanbanBacklogs />,
          // element: <ComingSoon />,
        },
        {
          path: 'board',
          element: <AppKanbanBoard />,
          // element: <ComingSoon />,
        },
      ],
    },
    {
      path: '/calendar',
      element: <AppCalendar />,
      // element: <ComingSoon />,
    },
    {
      path: '/docs-sign',
      element: <DocsSign />,
    },
    {
      path: '/external-app/:appId',
      element: <ExternalAppPage />,
    },
    {
      path: '/mail-whatsapp',
      element: <ComingSoon />,
    },
    {
      path: '/data-export',
      element: (
        <PermissionGuard module="dataExport">
          <ComingSoon />
        </PermissionGuard>
      ),
    },
    {
      path: '/statistics',
      element: <Navigate to="/boards" replace />,
    },
    {
      path: '/boards',
      element: <PermissionGuard module="dashboards"><BoardsLayout /></PermissionGuard>,
      children: [
        { index: true, element: <BoardListPage /> },
        { path: ':boardId', element: <BoardViewPage /> },
      ],
    },
    {
      path: '/info-center',
      element: <InfoCenter />,
    },
    {
      path: '/qa-master',
      element: <QAMasterPage />,
    },
    {
      path: '/map-insights',
      element: <MapVisualizerPage />,
    },
  ]),
};

export default MainRoutes;
